import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { IExtraOptions } from './AppDropdownWithPagination'
import { IWithPaginationRequestSearch } from '../../core/api/dto/BaseDto'
import { resetPaginationByParamsChanged } from '../../utils/resetPaginationByParamsChanged'
import { AppMultiDropdownWithPagination } from './AppMultiDropdownWithPagination'
import { LIMIT as DEFAULT_LIMIT } from 'utils/constants'

export interface IAppFilterDropdown<T, TKey> {
  searchParam: string
  propToShowInList: TKey
  sparePropToShowInList?: TKey
  extraPropToShowInList?: TKey
  valuePropName: TKey
  placeholder?: string
  label?: string
  id?: string
  defaultValue?: boolean
  shouldResetPagination?: boolean
  disabled?: boolean
  fullWidth?: boolean
  minWidth?: boolean
  emptyListMessage?: string
  getterData: (
    params: IWithPaginationRequestSearch | null | any | { multi: string },
    extraParams?: { skip?: boolean },
  ) => any
  extraOptions?: IExtraOptions
  setCurrentValue?: (value: T[]) => void
  labelList?: string
  showArchivedItems?: boolean //контролирует показ архивированных позиций, по умолчанию true -  показываем все
  limit?: number
  apiFilterParam?: string // имя параметра для API-запроса, если отличается от searchParam
}

export const AppFilterMultiDropdownWithPagination = <T, TKey extends keyof T>({
  searchParam,
  getterData,
  propToShowInList,
  extraPropToShowInList,
  sparePropToShowInList,
  valuePropName,
  label,
  placeholder,
  shouldResetPagination = true,
  extraOptions,
  setCurrentValue,
  emptyListMessage,
  disabled,
  fullWidth,
  minWidth,
  id,
  defaultValue,
  labelList,
  showArchivedItems,
  limit = DEFAULT_LIMIT,
  apiFilterParam,
}: PropsWithChildren<IAppFilterDropdown<T, TKey>>) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { pathname, state } = useLocation()
  const currentSearchParamValue = searchParams.getAll(
    searchParam,
  ) as unknown as TKey[]
  const currentSearchParamKey = currentSearchParamValue.join(',')

  // Инициализируем сразу из URL чтобы placeholder "Выбрано: N" отображался без задержки
  const [currentFilterValue, setCurrentFilterValue] = useState<T[]>(() =>
    currentSearchParamKey
      ? currentSearchParamKey
          .split(',')
          .map((id) => ({ [valuePropName]: id } as unknown as T))
      : [],
  )
  const [firstOpen, setFirstOpen] = useState<boolean>(true)

  // Запрос для восстановления выбранных значений по valuePropName из URL при монтировании/возврате назад
  const { data: selectedItemsData } = getterData(
    currentSearchParamKey
      ? {
          ...extraOptions,
          [apiFilterParam || searchParam]:
            currentSearchParamValue.length > 1
              ? currentSearchParamValue
              : currentSearchParamKey,
          limit: Math.max(currentSearchParamValue.length, DEFAULT_LIMIT),
          ...(showArchivedItems ? {} : { is_archived: false }),
        }
      : null,
    { skip: !currentSearchParamKey },
  )

  // Запрос для defaultValue — первый элемент списка когда нет параметров в URL
  const { data: defaultItemsData } = getterData(
    !currentSearchParamKey && defaultValue && firstOpen && !state
      ? {
          ...extraOptions,
          limit: 1,
          ...(showArchivedItems ? {} : { is_archived: false }),
        }
      : null,
    { skip: !(!currentSearchParamKey && defaultValue && firstOpen && !state) },
  )

  useEffect(() => {
    if (!currentSearchParamKey) {
      setCurrentFilterValue([])
      setCurrentValue && setCurrentValue([])
      return
    }
    if (!selectedItemsData?.results?.length || !valuePropName) return
    const ids = currentSearchParamKey.split(',')
    const matched = selectedItemsData.results.filter((item: T) =>
      ids.some((i) => i === `${item[valuePropName]}`),
    )
    // Текущие значения — "заглушки" из useState (только с ID, без полных данных объекта)
    const currentValuesAreStubs = currentFilterValue.every(
      (item) => Object.keys(item as object).length === 1,
    )
    const returnedIds = matched
      .map((item: T) => `${item[valuePropName]}`)
      .sort()
    const expectedIds = [...ids].sort()
    const dataMatchesUrl = returnedIds.join(',') === expectedIds.join(',')
    if (
      matched.length === ids.length &&
      (currentValuesAreStubs || dataMatchesUrl)
    ) {
      setCurrentFilterValue(matched)
      setCurrentValue && setCurrentValue(matched)
    }
    setFirstOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemsData, currentSearchParamKey])



  useEffect(() => {
    if (!defaultItemsData?.results?.length) return
    const defaultData = defaultItemsData.results[0]
    const defaultSearch = defaultData[valuePropName]
    if (!defaultSearch) return
    searchParams.set(searchParam, String(defaultSearch))
    setSearchParams(searchParams.toString(), { replace: true })
    setCurrentFilterValue([defaultData])
    setCurrentValue && setCurrentValue([defaultData])
    setFirstOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultItemsData])

  const changeHandler = useCallback(
    (item: T[]) => {
      const newParams = new URLSearchParams(searchParams.toString())
      if (!!item.length) {
        newParams.delete(searchParam)
        item.forEach((i) => {
          newParams.append(searchParam, String(i[valuePropName]))
        })
      } else {
        newParams.delete(searchParam)
      }
      setCurrentFilterValue(item)
      if (shouldResetPagination) {
        resetPaginationByParamsChanged({
          searchParams: newParams,
          setSearchParams,
          pathname,
        })
      } else {
        setSearchParams(newParams.toString(), { replace: true })
      }
    },
    [
      searchParams,
      setSearchParams,
      searchParam,
      valuePropName,
      pathname,
      shouldResetPagination,
    ],
  )

  const resetFilterHandler = useCallback(() => {
    searchParams.delete(searchParam)
    setSearchParams(searchParams.toString())
    setCurrentFilterValue([])
    setCurrentValue && setCurrentValue([])
    if (shouldResetPagination) {
      resetPaginationByParamsChanged({
        searchParams,
        setSearchParams,
        pathname,
      })
    }
  }, [
    searchParams,
    setSearchParams,
    searchParam,
    pathname,
    setCurrentValue,
    shouldResetPagination,
  ])

  return (
    <AppMultiDropdownWithPagination
      getterData={getterData}
      propToShowInList={propToShowInList}
      propToShowInInput={valuePropName}
      value={currentFilterValue}
      placeholder={placeholder}
      onChange={changeHandler}
      label={label}
      resetValueHandler={resetFilterHandler}
      extraOptions={extraOptions}
      disabled={disabled}
      emptyListMessage={emptyListMessage}
      fullWidth={fullWidth}
      minWidth={minWidth}
      extraPropToShowInList={extraPropToShowInList}
      sparePropToShowInList={sparePropToShowInList}
      id={`-f${id}`}
      showArchivedItems={showArchivedItems}
      limit={limit}
    />
  )
}
